<template>
  <v-snackbar
    v-model="showSnackbar"
    :color="currentNotification?.type"
    :timeout="-1"
    location="top right"
    variant="elevated"
    @update:model-value="onSnackbarClose"
  >
    <div class="d-flex align-center">
      <v-icon :icon="getNotificationIcon(currentNotification?.type)" class="me-3" />
      <span>{{ displayMessage }}</span>
    </div>

    <template v-if="currentNotification?.action" #actions>
      <v-btn
        :color="getActionColor(currentNotification.type)"
        variant="text"
        size="small"
        @click="handleAction"
      >
        {{ currentNotification.action.label }}
      </v-btn>
    </template>
  </v-snackbar>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useNotificationStore } from '@/stores/notification';
import type { NotificationType } from '@/stores/notification';

/** Only translate messages that are known api keys (e.g. from http interceptor). Never pass backend text to t() to avoid ICU SyntaxError. */
const KNOWN_API_KEY = /^api\.(success|errors)\.[a-zA-Z]+$/;

const notificationStore = useNotificationStore();
const { t } = useI18n();
const showSnackbar = ref(false);
let timeoutId: number | null = null;

const currentNotification = computed(() => {
  return notificationStore.notifications[0] || null;
});

const displayMessage = computed(() => {
  const msg = currentNotification.value?.message ?? '';
  if (!msg || !KNOWN_API_KEY.test(msg)) return msg;
  try {
    return t(msg);
  } catch {
    return msg;
  }
});

const getNotificationIcon = (type?: NotificationType): string => {
  switch (type) {
    case 'success':
      return 'mdi-check-circle';
    case 'error':
      return 'mdi-alert-circle';
    case 'warning':
      return 'mdi-alert';
    case 'info':
    default:
      return 'mdi-information';
  }
};

const getActionColor = (type?: NotificationType): string => {
  switch (type) {
    case 'success':
      return 'success';
    case 'error':
      return 'error';
    case 'warning':
      return 'warning';
    case 'info':
    default:
      return 'primary';
  }
};

const handleAction = () => {
  if (currentNotification.value?.action) {
    currentNotification.value.action.handler();
  }
  onSnackbarClose();
};

const onSnackbarClose = () => {
  // Clear any pending timeout
  if (timeoutId) {
    clearTimeout(timeoutId);
    timeoutId = null;
  }

  if (currentNotification.value) {
    notificationStore.removeNotification(currentNotification.value.id);
  }
  showSnackbar.value = false;
};

// Watch for new notifications
watch(
  () => notificationStore.notifications.length,
  (newLength, oldLength) => {
    if (newLength > oldLength && currentNotification.value) {
      showSnackbar.value = true;

      // Clear any existing timeout
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      // Set timeout to auto-close
      const duration = currentNotification.value.duration || 4000;
      timeoutId = window.setTimeout(() => {
        if (showSnackbar.value) {
          onSnackbarClose();
        }
      }, duration);
    }
  },
  { immediate: true }
);
</script>
